import { useRef, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useExperimentFileUpload from '../useExperimentFileUpload';
import { updateFileCategory } from '../../registryApi';
import { DOCUMENT_CATEGORY } from '../../registryChoices';

const MAX_UPLOAD_BYTES = 1024 ** 3;

/**
 * Drop zone + uploaded-file list for one kind of experiment file (data file, additional
 * documents, or one treatment's creatives). The zone follows FileSchemaEditor's
 * InferFromFileZone pattern; uploads go straight to GCS via signed URLs.
 *
 * `multiple={false}` (the data file) replaces the existing file instead of adding one.
 * `showCategory` adds the per-file document-type select (additional documents only);
 * `requireCategory` highlights any uploaded document still missing a type.
 * `accept` limits file types (e.g. ".csv"); it's enforced on drop too, since the browser
 * only applies it to the file picker. `beforeUpload` runs once before uploading (e.g. to
 * save answers the server checks the file against). `validateFile(file)` resolves to an error
 * message for a file that shouldn't be uploaded (or '' if it's fine), checked before
 * anything is sent. `disabledReason` explains a locked zone.
 */
export default function FileDropList({
  experimentId,
  kind,
  treatmentId,
  files,
  onChange,
  multiple = true,
  showCategory = false,
  requireCategory = false,
  accept,
  beforeUpload,
  validateFile,
  disabled = false,
  disabledReason = '',
  error: fieldError,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState('');
  const { uploadFile, removeFile, uploading, error: uploadError } = useExperimentFileUpload({
    experimentId,
    kind,
    treatmentId,
  });

  const allowedExtensions = accept ? accept.split(',').map((ext) => ext.trim().toLowerCase()) : null;
  const isAllowedType = (file) =>
    !allowedExtensions || allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

  const handleFiles = async (fileList) => {
    setLocalError('');
    let selected = Array.from(fileList || []);
    if (!multiple) selected = selected.slice(0, 1);
    const problems = [];
    const wrongType = selected.filter((file) => !isAllowedType(file));
    if (wrongType.length) {
      problems.push(`Only ${accept} files can be uploaded here: ${wrongType.map((f) => f.name).join(', ')}`);
    }
    const tooLarge = selected.filter((file) => file.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length) {
      problems.push(`Over the 1GB limit: ${tooLarge.map((f) => f.name).join(', ')}`);
    }
    selected = selected.filter((file) => isAllowedType(file) && file.size <= MAX_UPLOAD_BYTES);
    if (validateFile && selected.length) {
      const results = await Promise.all(
        selected.map((file) => validateFile(file).catch(() => `${file.name}: could not be read.`))
      );
      problems.push(...results.filter(Boolean));
      selected = selected.filter((_, index) => !results[index]);
    }
    if (problems.length) setLocalError(problems.join(' '));
    if (!selected.length) return;

    if (beforeUpload) {
      try {
        await beforeUpload();
      } catch (e) {
        setLocalError(e.message || 'Could not save your answers before uploading.');
        return;
      }
    }

    let current = files;
    for (const file of selected) {
      const confirmed = await uploadFile(file).catch(() => null);
      if (!confirmed) continue;
      if (!multiple) {
        for (const old of current) {
          await removeFile(old.id).catch(() => null);
        }
        current = [];
      }
      current = [...current, confirmed];
      onChange(current);
    }
  };

  const handleRemove = async (fileId) => {
    await removeFile(fileId).catch(() => null);
    onChange(files.filter((f) => f.id !== fileId));
  };

  const handleCategoryChange = async (fileId, category) => {
    setLocalError('');
    try {
      const updated = await updateFileCategory(fileId, category);
      onChange(files.map((f) => (f.id === fileId ? updated : f)));
    } catch (e) {
      setLocalError(e.message);
    }
  };

  const message = localError || uploadError;
  const isLocked = disabled || Boolean(disabledReason);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {message && <Alert severity="error">{message}</Alert>}

      <Box
        role="button"
        tabIndex={0}
        aria-disabled={isLocked}
        onClick={() => !isLocked && !uploading && inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && !isLocked && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isLocked) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!isLocked && !uploading) handleFiles(e.dataTransfer.files);
        }}
        sx={{
          border: 1,
          borderStyle: 'dashed',
          borderColor: fieldError ? 'error.main' : dragOver ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          py: 2.5,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked ? 0.6 : 1,
          backgroundColor: dragOver ? 'action.hover' : 'grey.50',
          transition: 'all 0.15s',
        }}
      >
        {uploading ? (
          <>
            <CircularProgress size={22} />
            <Typography variant="caption" color="text.secondary">
              Uploading…
            </Typography>
          </>
        ) : (
          <>
            <UploadFileOutlinedIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              Drag &amp; drop {multiple ? 'files' : 'a file'} here, or{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                click to browse
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {accept ? `${accept} files only. ` : 'Any file type. '}Max file upload size of 1GB.
            </Typography>
            {disabledReason && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {disabledReason}
              </Typography>
            )}
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </Box>

      {files.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider' }}>
          {files.map((file, index) => (
            <Box
              key={file.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                borderTop: index === 0 ? 0 : 1,
                borderColor: 'divider',
              }}
            >
              <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ flex: 1, color: 'text.dark', wordBreak: 'break-all' }}>
                {file.original_filename}
              </Typography>
              {showCategory && (
                <TextField
                  select
                  hiddenLabel
                  size="small"
                  value={file.document_category || ''}
                  onChange={(e) => handleCategoryChange(file.id, e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  disabled={disabled}
                  error={requireCategory && !file.document_category}
                  sx={{ width: 220 }}
                >
                  <MenuItem value="" disabled>
                    Document type…
                  </MenuItem>
                  {DOCUMENT_CATEGORY.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <IconButton
                size="small"
                color="error"
                aria-label={`Remove ${file.original_filename}`}
                disabled={disabled || uploading}
                onClick={() => handleRemove(file.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
